"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.QueryManager = void 0;
const lodash_1 = require("lodash");
const track_changes_1 = require("@sierralabs/track-changes");
class QueryManager {
    constructor(query, entities, plugins = []) {
        this.entities = entities;
        this.plugins = plugins;
        this.subscriptions = [];
        this.cachedQueryAliasColumnMap = [];
        this.trackChanges = new track_changes_1.TrackChanges(query);
        this.cacheQueryAliasColumnMap(entities);
        this.subscribe('change', () => this.cacheQueryAliasColumnMap(entities));
    }
    get query() {
        return this.trackChanges.final;
    }
    get history() {
        return this.trackChanges.history;
    }
    /**
     * A map of query aliases for entities and columns as well as error
     * validation handling.
     */
    get queryAliasColumnMap() {
        return this.cachedQueryAliasColumnMap;
    }
    get(path) {
        return this.trackChanges.get(path);
    }
    uncommit() {
        this.trackChanges.uncommit();
        this.notify('change', this.getLastDeltas());
        this.notify('selectChange', this.getLastDeltas());
        this.notify('fromChange', this.getLastDeltas());
        this.notify('joinChange', this.getLastDeltas());
        this.notify('whereChange', this.getLastDeltas());
        this.notify('groupByChange', this.getLastDeltas());
        this.notify('orderByChange', this.getLastDeltas());
    }
    commit() {
        return this.trackChanges.commit();
    }
    /**
     * Subscribe for events. Make sure you call unsubscribe to prevent memory leaks.
     */
    subscribe(event, handler) {
        this.subscriptions.push({ event, handler });
    }
    /**
     * Unsubscribe event mathing the handler.
     */
    unsubscribe(event, handler) {
        this.subscriptions
            .filter(s => s.event === event && s.handler === handler)
            .forEach(s => {
            const index = this.subscriptions.indexOf(s);
            this.subscriptions.splice(index, 1);
        });
    }
    notify(event, deltas) {
        for (const subscription of this.subscriptions) {
            if (subscription.event === event) {
                subscription.handler(deltas);
            }
        }
    }
    getLastDeltas(count = 1) {
        // get last number of deltas from undo
        return this.trackChanges.history.undo.slice(-count);
    }
    updateName(queryName) {
        var _a;
        (_a = this.trackChanges) === null || _a === void 0 ? void 0 : _a.update('name', queryName);
        this.notify('change', this.getLastDeltas());
    }
    updateFrom(entity, path = 'config') {
        var _a;
        (_a = this.trackChanges) === null || _a === void 0 ? void 0 : _a.update(path, {
            mainAlias: {
                type: 'from',
                name: lodash_1.camelCase(entity.tableName),
                tablePath: entity.tableName
            }
        });
        this.notify('fromChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    updateFromAlias(alias, path = 'config.mainAlias.name') {
        var _a;
        (_a = this.trackChanges) === null || _a === void 0 ? void 0 : _a.update(path, alias);
        this.notify('fromChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    addSelect(select) {
        var _a, _b;
        if (!((_b = (_a = this.trackChanges.final) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.selects)) {
            this.trackChanges.update('config.selects', []);
        }
        this.trackChanges.insert('config.selects', select);
        this.notify('selectChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    updateSelect(index, select, path = 'config.selects') {
        if (!this.trackChanges.get(path)) {
            this.trackChanges.update(path, []);
        }
        this.trackChanges.update(`${path}[${index}]`, select);
        this.notify('selectChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    moveSelect(sourceIndex, targetIndex, path = 'config.selects') {
        this.trackChanges.move(path, sourceIndex, targetIndex);
        this.notify('selectChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    deleteSelect(index, path = 'config.selects') {
        this.trackChanges.delete(`${path}[${index}]`);
        this.notify('selectChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    addJoin(joinAttribute, path = 'config.joinAttributes') {
        if (!this.trackChanges.get(path)) {
            this.trackChanges.update(path, []);
        }
        this.trackChanges.insert(path, joinAttribute);
        this.notify('joinChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    updateJoinCondition(index, condition, path = 'config.joinAttributes') {
        this.trackChanges.update(`${path}[${index}].condition`, condition);
        this.notify('joinChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    updateJoinDirection(index, direction, path = 'config.joinAttributes') {
        this.trackChanges.update(`${path}[${index}].direction`, direction);
        this.notify('joinChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    updateJoinEntityProperty(index, property, path = 'config.joinAttributes') {
        this.trackChanges.update(`${path}[${index}].entityOrProperty`, property);
        this.notify('joinChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    updateJoinAlias(index, alias, path = 'config.joinAttributes') {
        this.trackChanges.update(`${path}[${index}].alias`, alias);
        this.notify('joinChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    updateJoinAliasName(index, aliasName, path = 'config.joinAttributes') {
        var _a;
        (_a = this.trackChanges) === null || _a === void 0 ? void 0 : _a.update(`${path}[${index}].alias.name`, aliasName);
        this.notify('joinChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    deleteJoin(index, path = 'config.joinAttributes') {
        this.trackChanges.delete(`${path}[${index}]`);
        this.notify('joinChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    addWhere(where) {
        const wheres = this.trackChanges.get('config.wheres');
        if (!wheres) {
            this.trackChanges.update('config.wheres', []);
        }
        if (where) {
            this.trackChanges.insert('config.wheres', where);
        }
        this.notify('whereChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    updateWhere(index, where, path = 'config.wheres') {
        const wheres = this.trackChanges.get(path);
        if (!wheres) {
            this.trackChanges.update(path, []);
        }
        if (where) {
            this.trackChanges.update(`${path}[${index}]`, where);
        }
        this.notify('whereChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    deleteWhere(index, path = 'config.wheres') {
        this.trackChanges.delete(`${path}[${index}]`);
        this.notify('whereChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    updateWhereType(index, type, path = 'config.wheres') {
        this.trackChanges.update(`${path}[${index}].type`, type);
        this.notify('whereChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    addGroupBy(value, path = 'config.groupBys') {
        if (!this.trackChanges.get(path)) {
            this.trackChanges.update(path, []);
        }
        this.trackChanges.insert(path, value);
        this.notify('groupByChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    updateGroupBy(index, value, path = 'config.groupBys') {
        if (!this.trackChanges.get(path)) {
            this.trackChanges.update(path, []);
        }
        this.trackChanges.update(`${path}[${index}]`, value);
        this.notify('groupByChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    moveGroupBy(sourceIndex, targetIndex, path = 'config.groupBys') {
        this.trackChanges.move(path, sourceIndex, targetIndex);
        this.notify('groupByChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    deleteGroupBy(index, path = 'config.groupBys') {
        this.trackChanges.delete(`${path}[${index}]`);
        this.notify('groupByChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    addOrderBy(orderBy, path = 'config.orderBys') {
        if (!this.trackChanges.get(path)) {
            this.trackChanges.update(path, []);
        }
        this.trackChanges.insert(path, orderBy);
        this.notify('orderByChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    updateOrderBy(index, orderBy) {
        var _a, _b;
        if (!((_b = (_a = this.trackChanges.final) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.orderBys)) {
            this.trackChanges.update('config.orderBys', []);
        }
        this.trackChanges.update(`config.orderBys[${index}]`, orderBy);
        this.notify('orderByChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    updateOrderByDirection(index, direction, path = 'config.orderBys') {
        this.trackChanges.update(`${path}[${index}].order`, direction);
        this.notify('orderByChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    updateOrderByNulls(index, value, path = 'config.orderBys') {
        this.trackChanges.update(`${path}[${index}].nulls`, value);
        this.notify('orderByChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    updateOrderByPropertyPath(index, propertyPath, path = 'config.orderBys') {
        this.trackChanges.update(`${path}[${index}].propertyPath`, propertyPath);
        this.notify('orderByChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    moveOrderBy(sourceIndex, targetIndex) {
        this.trackChanges.move('config.orderBys', sourceIndex, targetIndex);
        this.notify('orderByChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    deleteOrderBy(index, path = 'config.orderBys') {
        this.trackChanges.delete(`${path}[${index}]`);
        this.notify('orderByChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    addPluginConfig(pluginConfig) {
        var _a, _b, _c;
        if (!((_b = (_a = this.trackChanges.final) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.pluginConfigs)) {
            this.trackChanges.update('config.pluginConfigs', []);
        }
        const plugin = this.plugins.find(p => p.type === (pluginConfig === null || pluginConfig === void 0 ? void 0 : pluginConfig.type));
        (_c = plugin === null || plugin === void 0 ? void 0 : plugin.init) === null || _c === void 0 ? void 0 : _c.call(plugin, this);
        this.trackChanges.insert(`config.pluginConfigs`, pluginConfig);
        this.notify('pluginConfigChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    updatePluginConfig(index, pluginConfig) {
        var _a, _b;
        if (!((_b = (_a = this.trackChanges.final) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.pluginConfigs)) {
            this.trackChanges.update('config.pluginConfigs', []);
        }
        this.trackChanges.update(`config.pluginConfigs[${index}]`, pluginConfig);
        this.notify('pluginConfigChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    deletePluginConfig(index) {
        var _a, _b;
        const pluginConfig = (_b = (_a = this.query.config) === null || _a === void 0 ? void 0 : _a.pluginConfigs) === null || _b === void 0 ? void 0 : _b[index];
        const plugin = this.plugins.find(p => p.type === (pluginConfig === null || pluginConfig === void 0 ? void 0 : pluginConfig.type));
        if (plugin === null || plugin === void 0 ? void 0 : plugin.deinit)
            plugin.deinit(this);
        this.trackChanges.delete(`config.pluginConfigs[${index}]`);
        this.notify('pluginConfigChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    addAndPluginCondition(pluginCondition) {
        var _a, _b;
        if (!((_b = (_a = this.trackChanges.final) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.pluginConditions)) {
            this.trackChanges.update('config.pluginConditions', []);
        }
        this.trackChanges.insert(`config.pluginConditions`, [pluginCondition]);
        this.notify('pluginConfigConditionChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    addOrPluginCondition(andIndex, pluginCondition) {
        var _a, _b, _c;
        if (!((_c = (_b = (_a = this.trackChanges.final) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.pluginConditions) === null || _c === void 0 ? void 0 : _c[andIndex])) {
            this.trackChanges.update(`config.pluginConditions[${andIndex}]`, []);
        }
        this.trackChanges.insert(`config.pluginConditions.${andIndex}`, // need to use dot notation due to trackChanges.parsePathArray issue
        pluginCondition);
        this.notify('pluginConfigConditionChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    updatePluginCondition(andIndex, orIndex, pluginCondition) {
        this.trackChanges.update(`config.pluginConditions[${andIndex}][${orIndex}]`, pluginCondition);
        this.notify('pluginConfigConditionChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    deletePluginCondition(andIndex, orIndex) {
        var _a, _b, _c, _d;
        this.trackChanges.delete(`config.pluginConditions[${andIndex}][${orIndex}]`);
        if (((_d = (_c = (_b = (_a = this.trackChanges.final) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.pluginConditions) === null || _c === void 0 ? void 0 : _c[andIndex]) === null || _d === void 0 ? void 0 : _d.length) ===
            0) {
            this.trackChanges.delete(`config.pluginConditions[${andIndex}]`);
        }
        this.notify('pluginConfigConditionChange', this.getLastDeltas());
        this.notify('change', this.getLastDeltas());
    }
    getQueryAliasColumnMap(config, entities) {
        var _a, _b;
        if (config === void 0) { config = (_a = this.query) === null || _a === void 0 ? void 0 : _a.config; }
        if (entities === void 0) { entities = this.entities; }
        // console.log('cacheQueryAliasColumnMap', entities);
        const mainAlias = config === null || config === void 0 ? void 0 : config.mainAlias;
        const fromEntity = (mainAlias === null || mainAlias === void 0 ? void 0 : mainAlias.tablePath)
            ? entities === null || entities === void 0 ? void 0 : entities.find(e => e.tableName === (mainAlias === null || mainAlias === void 0 ? void 0 : mainAlias.tablePath))
            : undefined;
        const queryAliasColumnMap = [];
        if ((mainAlias === null || mainAlias === void 0 ? void 0 : mainAlias.name) && (fromEntity === null || fromEntity === void 0 ? void 0 : fromEntity.columns)) {
            queryAliasColumnMap.push(...fromEntity.columns
                // don't show fields that "explicitly" have select === false
                .filter(column => column.select !== false)
                .map(column => ({
                path: `${mainAlias.name}.${this.getColumnName(column)}`,
                alias: mainAlias.name,
                entity: fromEntity,
                column
            })));
        }
        // add join attribute fields
        (_b = config === null || config === void 0 ? void 0 : config.joinAttributes) === null || _b === void 0 ? void 0 : _b.forEach(attribute => {
            if (!attribute.alias)
                return;
            const tablePath = attribute.alias.tablePath;
            const entity = entities === null || entities === void 0 ? void 0 : entities.find(e => e.tableName === tablePath);
            if (entity === null || entity === void 0 ? void 0 : entity.columns)
                queryAliasColumnMap.push(...entity.columns
                    // don't show fields that "explicitly" have select === false
                    .filter(column => column.select !== false)
                    .map(column => ({
                    path: `${attribute.alias.name}.${this.getColumnName(column)}`,
                    alias: attribute.alias.name,
                    entity,
                    column
                })));
            // add join column maps from subqueries
            const subQuery = attribute.alias.subQuery && typeof attribute.alias.subQuery === 'object'
                ? attribute.alias.subQuery
                : undefined;
            queryAliasColumnMap.push(...this.getJoinConfigColumnMaps(subQuery, entities, attribute.alias.name));
        });
        // add plugin columns
        // console.log('caching plugin columns', fromEntity, this.plugins);
        if (fromEntity) {
            for (const pluginConfig of (config === null || config === void 0 ? void 0 : config.pluginConfigs) || []) {
                const plugin = this.plugins.find(p => p.type === pluginConfig.type);
                if (!plugin)
                    continue;
                // const columns =
                //   typeof plugin.columns === 'function'
                //     ? plugin.columns(this.query, this.entities)
                //     : plugin.columns;
                queryAliasColumnMap.push(...(plugin.columns || []).map(column => ({
                    path: column.selection,
                    alias: column.aliasName || column.name,
                    entity: fromEntity,
                    plugin,
                    column: {
                        displayName: column.name,
                        name: column.name,
                        type: column.type
                    }
                })));
            }
        }
        return queryAliasColumnMap;
    }
    /**
     * Create a map of query aliases for entities and columns as well as error
     * validation handling.
     */
    cacheQueryAliasColumnMap(entities) {
        this.cachedQueryAliasColumnMap = this.getQueryAliasColumnMap(undefined, entities);
    }
    getColumnName(column) {
        return column.name || lodash_1.camelCase(column.displayName);
    }
    getJoinConfigColumnMaps(config, entities, subAlias) {
        var _a;
        const mainAlias = config === null || config === void 0 ? void 0 : config.mainAlias;
        const entity = (mainAlias === null || mainAlias === void 0 ? void 0 : mainAlias.tablePath)
            ? entities === null || entities === void 0 ? void 0 : entities.find(e => e.tableName === (mainAlias === null || mainAlias === void 0 ? void 0 : mainAlias.tablePath))
            : undefined;
        const queryAliasColumnMap = [];
        for (const select of (config === null || config === void 0 ? void 0 : config.selects) || []) {
            const [alias, property] = select.selection.split('.');
            // propertyPath will include the alias name
            // e.g. productUserNeed.taskId -> productUserNeed_task_id
            const propertyPath = select.aliasName || `${alias}_${lodash_1.snakeCase(property)}`;
            const column = entity && lodash_1.camelCase(entity.name) === alias
                ? entity.columns.find(column => property === this.getColumnName(column))
                : undefined;
            if (entity && column) {
                queryAliasColumnMap.push({
                    path: `${subAlias}.${propertyPath}`,
                    alias: subAlias,
                    entity,
                    column
                });
                continue;
            }
            // handle joins
            const attribute = (_a = config === null || config === void 0 ? void 0 : config.joinAttributes) === null || _a === void 0 ? void 0 : _a.find(attribute => attribute.alias.name === alias);
            if (!attribute) {
                continue;
            }
            // if subQuery, handle recursively
            const { name, subQuery, tablePath } = attribute.alias;
            if (subQuery && typeof subQuery === 'object') {
                queryAliasColumnMap.push(...this.getJoinConfigColumnMaps(subQuery, entities, name));
                continue;
            }
            const joinEntity = tablePath
                ? entities === null || entities === void 0 ? void 0 : entities.find(e => e.tableName === tablePath)
                : undefined;
            const joinColumn = joinEntity === null || joinEntity === void 0 ? void 0 : joinEntity.columns.find(column => property === this.getColumnName(column));
            if (joinEntity && joinColumn) {
                queryAliasColumnMap.push({
                    path: `${subAlias}.${propertyPath}`,
                    alias: subAlias,
                    entity: joinEntity,
                    column: joinColumn
                });
            }
        }
        return queryAliasColumnMap;
    }
}
exports.QueryManager = QueryManager;
//# sourceMappingURL=QueryManager.js.map